/**
 * Native Wishlist - Clean localStorage-based wishlist implementation
 * Works seamlessly with variant shuffle and all product cards
 */

class NativeWishlist {
  constructor() {
    this.storageKey = 'justyol_wishlist';
    this.items = this.loadWishlist();
    this.locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'fr';
    this.strings = this.locale === 'ar'
      ? { added: 'تمت الإضافة إلى المفضلة', removed: 'تمت الإزالة من المفضلة', cleared: 'تم مسح قائمة الأمنيات' }
      : { added: 'Ajouté aux favoris', removed: 'Retiré des favoris', cleared: 'Liste de souhaits vidée' };
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }

    // Listen for variant shuffle completion
    window.addEventListener('variantShuffle:complete', () => {
      this.attachEventListeners();
      this.updateAllHearts();
      this.updateCounts();
    });

    // Listen for dynamically loaded content (recently viewed, recommendations, etc.)
    document.addEventListener('recommendations:loaded', () => {
      this.attachEventListeners();
      this.updateAllHearts();
      this.updateCounts();
    });

    // Also listen on any recently-viewed-products element
    document.querySelectorAll('recently-viewed-products').forEach(el => {
      el.addEventListener('recommendations:loaded', () => {
        this.attachEventListeners();
        this.updateAllHearts();
        this.updateCounts();
      });
    });

    // Use MutationObserver as fallback for any dynamically added wishlist buttons
    this.observeDynamicContent();
  }

  // Observe DOM for dynamically added wishlist buttons
  observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      let hasNewButtons = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('wishlist-btn')) {
              hasNewButtons = true;
            } else if (node.querySelector && node.querySelector('.wishlist-btn:not([data-wishlist-initialized])')) {
              hasNewButtons = true;
            }
          }
        });
      });
      if (hasNewButtons) {
        this.attachEventListeners();
        this.updateAllHearts();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  setup() {
    this.attachEventListeners();
    this.updateAllHearts();
    this.updateCounts();
  }

  // Load wishlist from localStorage
  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  // Save wishlist to localStorage
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    } catch (e) {
      // Silent fail - localStorage may be full or disabled
    }
  }

  // Check if product/variant is in wishlist
  isInWishlist(productId, variantId = null) {
    return this.items.some(item => {
      if (variantId) {
        return item.productId === productId && item.variantId === variantId;
      }
      return item.productId === productId;
    });
  }

  // Add item to wishlist
  addToWishlist(productId, variantId, productHandle, productTitle, productImage, productPrice, productUrl, secondImage, vendor, compareAtPrice, productType, dropDate) {
    // Check if already exists
    if (this.isInWishlist(productId, variantId)) {
      return;
    }

    const item = {
      productId,
      variantId: variantId || null,
      productHandle,
      productTitle,
      productImage,
      productPrice,
      productUrl,
      secondImage: secondImage || null,
      vendor: vendor || null,
      compareAtPrice: compareAtPrice || null,
      productType: productType || null,
      dropDate: dropDate || null,
      addedAt: Date.now()
    };

    this.items.push(item);
    this.saveWishlist();
    this.updateCounts();
    this.updateAllHearts();

    this.showNotification(this.strings.added, 'added');

    // Dispatch event
    window.dispatchEvent(new CustomEvent('wishlist:added', { detail: item }));
  }

  // Remove item from wishlist
  removeFromWishlist(productId, variantId = null) {
    const initialLength = this.items.length;

    this.items = this.items.filter(item => {
      if (variantId) {
        return !(item.productId === productId && item.variantId === variantId);
      }
      return item.productId !== productId;
    });

    if (this.items.length !== initialLength) {
      this.saveWishlist();
      this.updateCounts();
      this.updateAllHearts();

      this.showNotification(this.strings.removed, 'removed');

      // Dispatch event
      window.dispatchEvent(new CustomEvent('wishlist:removed', {
        detail: { productId, variantId }
      }));
    }
  }

  // Toggle wishlist status
  toggleWishlist(productId, variantId, productHandle, productTitle, productImage, productPrice, productUrl, secondImage, vendor, compareAtPrice, productType, dropDate) {
    if (this.isInWishlist(productId, variantId)) {
      this.removeFromWishlist(productId, variantId);
    } else {
      this.addToWishlist(productId, variantId, productHandle, productTitle, productImage, productPrice, productUrl, secondImage, vendor, compareAtPrice, productType, dropDate);
    }
  }

  // Attach event listeners to all wishlist buttons
  attachEventListeners() {
    document.querySelectorAll('.wishlist-btn:not([data-wishlist-initialized])').forEach(btn => {
      btn.setAttribute('data-wishlist-initialized', 'true');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Add loading state
        btn.classList.add('is-loading');

        const productId = btn.getAttribute('data-product-id');
        const variantId = btn.getAttribute('data-variant-id');
        const productHandle = btn.getAttribute('data-product-handle');
        const productTitle = btn.getAttribute('data-product-title');
        const productImage = btn.getAttribute('data-product-image');
        const productPrice = btn.getAttribute('data-product-price');
        const productUrl = btn.getAttribute('data-product-url');
        const secondImage = btn.getAttribute('data-product-second-image');
        const vendor = btn.getAttribute('data-product-vendor');
        const compareAtPrice = btn.getAttribute('data-product-compare-price');
        const productType = btn.getAttribute('data-product-type');
        const dropDate = btn.getAttribute('data-drop-date');

        if (!productId || !productHandle) {
          btn.classList.remove('is-loading');
          return;
        }

        // Small delay for animation feedback
        setTimeout(() => {
          this.toggleWishlist(
            productId,
            variantId,
            productHandle,
            productTitle,
            productImage,
            productPrice,
            productUrl,
            secondImage,
            vendor,
            compareAtPrice,
            productType,
            dropDate
          );
          btn.classList.remove('is-loading');
          // tactile "pop" only when the user just ADDED it (not on page load)
          if (btn.classList.contains('is-active')) {
            btn.classList.add('jy-pop');
            setTimeout(() => btn.classList.remove('jy-pop'), 420);
          }
        }, 150);
      });
    });
  }

  // Update all heart icons to reflect wishlist status
  updateAllHearts() {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const productId = btn.getAttribute('data-product-id');
      const variantId = btn.getAttribute('data-variant-id');

      if (this.isInWishlist(productId, variantId)) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  // Update wishlist count badges
  updateCounts() {
    const count = this.items.length;
    document.querySelectorAll('.wishlist-count').forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.style.display = '';
        el.classList.add('has-items');
      } else {
        el.style.display = 'none';
        el.classList.remove('has-items');
      }
    });
  }

  // Show notification toast (on-brand pill; styled in jy-base.css)
  showNotification(message, type) {
    const existing = document.querySelector('.wishlist-notification');
    if (existing) existing.remove();

    const heart =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
    const check =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    const icon = type === 'added' ? heart : check;

    const notification = document.createElement('div');
    notification.className = 'wishlist-notification' + (type ? ' wishlist-notification--' + type : '');
    notification.innerHTML = '<span class="wishlist-notification__ico">' + icon + '</span><span>' + message + '</span>';
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('is-leaving');
      setTimeout(() => notification.remove(), 280);
    }, 2200);
  }

  // Get all wishlist items
  getItems() {
    return this.items;
  }

  // Get wishlist count
  getCount() {
    return this.items.length;
  }

  // Clear entire wishlist
  clearWishlist() {
    this.items = [];
    this.saveWishlist();
    this.updateCounts();
    this.updateAllHearts();
    this.showNotification(this.strings.cleared, 'cleared');

    window.dispatchEvent(new CustomEvent('wishlist:cleared'));
  }
}

// Add animation styles
if (!document.getElementById('wishlist-animations')) {
  const style = document.createElement('style');
  style.id = 'wishlist-animations';
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
}

// Initialize and expose globally
if (typeof window !== 'undefined') {
  window.NativeWishlist = NativeWishlist;
  window.wishlist = new NativeWishlist();
}
